import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  BellOff,
  Calendar,
  Check,
  CheckCheck,
  CheckSquare,
  ChevronLeft,
  Eye,
  EyeOff,
  Home as HomeIcon,
  Search as SearchIcon,
  Sparkles,
  Square,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react-native';
import { ComponentType, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useConfirm } from '@/components/ConfirmDialog';
import { InfiniteFooter } from '@/components/InfiniteFooter';
import { SwipeRow } from '@/components/SwipeRow';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { useInfiniteWindow } from '@/hooks/use-infinite-window';
import { goBackOrTo, goTo } from '@/lib/nav';
import {
  dismissMessage,
  dismissMessages,
  markAllRead,
  markRead,
  markReadBulk,
  markUnread,
  useMessages,
  type AppMessage,
  type MessageTone,
} from '@/lib/messages';
import { listProperties } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

/**
 * Notifications inbox.
 *
 * Browsing layer:
 *   • Search bar (matches title + body)
 *   • Filter chips: All / Unread / Achievements / Alerts / Updates
 *   • Date-grouped sections (Today / Yesterday / This week / Earlier)
 *
 * Interaction layer:
 *   • Tap a row → expands body, marks read (if unread)
 *   • Long-press OR tap "Select" → enters bulk mode
 *   • Bulk topbar shows Mark read / Mark unread / Delete actions
 *   • Delete (single + bulk) shows a confirmation; the user can't lose
 *     a message in one accidental tap.
 *
 * Visual:
 *   • Unread rows: bold title + a clay accent dot on the left edge
 *   • Read rows: standard weight, slightly muted
 *   • Tone-tinted icon chip (achievement = honey, warning = clay, etc.)
 */

type FilterKey = 'all' | 'unread' | 'alerts' | 'updates';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  // Achievements filter removed alongside the Achievements UI. Any
  // historical 'achievement'-toned messages still in the inbox just
  // show up under All / Updates.
  { key: 'alerts', label: 'Alerts' },
  { key: 'updates', label: 'Updates' },
];

function passesFilter(m: AppMessage, f: FilterKey): boolean {
  switch (f) {
    case 'all':
      return true;
    case 'unread':
      return !m.read;
    case 'alerts':
      return m.tone === 'warning';
    case 'updates':
      return m.tone === 'info' || m.tone === 'success' || m.tone === 'achievement';
  }
}

const DAY_MS = 24 * 60 * 60 * 1000;

function bucketFor(ts: number): 'today' | 'yesterday' | 'this-week' | 'earlier' {
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (ts >= startToday) return 'today';
  if (ts >= startToday - DAY_MS) return 'yesterday';
  if (ts >= startToday - 7 * DAY_MS) return 'this-week';
  return 'earlier';
}

const BUCKET_LABEL: Record<ReturnType<typeof bucketFor>, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  'this-week': 'This week',
  earlier: 'Earlier',
};

export default function MessagesScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const confirm = useConfirm();
  const messages = useMessages();

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Multi-home: load every property nickname so MessageRow can show a
  // "from [home]" chip on messages tagged with a property_id. Only
  // matters when the user has 2+ homes — single-home users see no chip
  // at all (every message is implicitly from the only home).
  const [homesById, setHomesById] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    void (async () => {
      const props = await listProperties(db);
      if (props.length < 2) {
        // Single-home: skip the chip work entirely — no visual clutter
        // for users who don't have multiple homes to disambiguate.
        setHomesById(new Map());
        return;
      }
      const next = new Map<string, string>();
      for (const p of props) {
        next.set(p.id, p.name?.trim() || p.address?.trim() || 'Untitled');
      }
      setHomesById(next);
    })();
  }, [db]);

  // ── Filtered + grouped messages ─────────────────────────────────────
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return messages.filter((m) => {
      if (!passesFilter(m, filter)) return false;
      if (!q) return true;
      const hay = `${m.title} ${m.body ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [messages, filter, query]);

  // Window the filtered list before grouping. Messages can pile up over
  // months — even a moderately-active user racks up hundreds — and a
  // ScrollView painting every row hurts low-end Android. The full
  // `visible` set still drives unread counts and search hit detection;
  // we only window the render. Reset to 40 whenever the filter changes
  // so a user filtering to "Unread" never sees a stale "Loading more"
  // spinner.
  const win = useInfiniteWindow(visible, { pageSize: 25, initialSize: 20 });
  const grouped = useMemo(() => {
    const order: ReturnType<typeof bucketFor>[] = ['today', 'yesterday', 'this-week', 'earlier'];
    const buckets = new Map<ReturnType<typeof bucketFor>, AppMessage[]>();
    for (const m of win.visible) {
      const b = bucketFor(m.ts);
      if (!buckets.has(b)) buckets.set(b, []);
      buckets.get(b)!.push(m);
    }
    return order
      .filter((b) => buckets.has(b))
      .map((b) => ({ key: b, label: BUCKET_LABEL[b], items: buckets.get(b)! }));
  }, [win.visible]);

  const unreadCount = useMemo(
    () => messages.filter((m) => !m.read).length,
    [messages]
  );
  const totalCount = messages.length;

  // ── Row interactions ───────────────────────────────────────────────
  const onRowPress = useCallback(
    (m: AppMessage) => {
      if (selectMode) {
        setSelected((prev) => {
          const next = new Set(prev);
          if (next.has(m.id)) next.delete(m.id);
          else next.add(m.id);
          return next;
        });
        return;
      }
      // Mark read on first interaction regardless of which branch we
      // take below. Fire-and-forget; the listener refreshes the cache
      // when the DB round-trip lands.
      if (!m.read) void markRead(db, m.id);
      // If the message has a ctaRoute (schema v32), tap routes there
      // instead of expanding the inbox detail. Used by the welcome
      // seed to deep-link into /profile-setup.
      if (m.ctaRoute) {
        router.push(m.ctaRoute as never);
        return;
      }
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(m.id)) next.delete(m.id);
        else next.add(m.id);
        return next;
      });
    },
    [db, router, selectMode]
  );

  const onRowLongPress = useCallback(
    (m: AppMessage) => {
      if (!selectMode) {
        setSelectMode(true);
        setSelected(new Set([m.id]));
      }
    },
    [selectMode]
  );

  const onDeleteOne = useCallback(
    async (m: AppMessage) => {
      const ok = await confirm({
        title: 'Delete this message?',
        message: m.title,
        destructive: true,
        confirmLabel: 'Delete',
      });
      if (!ok) return;
      await dismissMessage(db, m.id);
    },
    [confirm, db]
  );

  // ── Bulk actions ───────────────────────────────────────────────────
  const exitSelect = useCallback(() => {
    setSelectMode(false);
    setSelected(new Set());
  }, []);

  const bulkMarkRead = useCallback(
    async (read: boolean) => {
      const ids = Array.from(selected);
      if (ids.length === 0) return;
      await markReadBulk(db, ids, read);
      exitSelect();
    },
    [db, exitSelect, selected]
  );

  const bulkDelete = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const ok = await confirm({
      title: `Delete ${ids.length} message${ids.length === 1 ? '' : 's'}?`,
      message: "This can't be undone.",
      destructive: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    await dismissMessages(db, ids);
    exitSelect();
  }, [confirm, db, exitSelect, selected]);

  const onMarkAllRead = useCallback(async () => {
    if (unreadCount === 0) return;
    await markAllRead(db);
  }, [db, unreadCount]);

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        {selectMode ? (
          <BulkTopbar
            count={selected.size}
            onCancel={exitSelect}
            onMarkRead={() => bulkMarkRead(true)}
            onMarkUnread={() => bulkMarkRead(false)}
            onDelete={bulkDelete}
          />
        ) : (
          <BrowseTopbar
            router={router}
            unreadCount={unreadCount}
            onSelect={() => setSelectMode(true)}
            onMarkAllRead={onMarkAllRead}
            disableBulkActions={totalCount === 0}
          />
        )}
      </View>

      {totalCount > 0 ? (
        <>
          <View style={styles.searchBox}>
            <SearchIcon size={15} color={Palette.inkSoft} strokeWidth={2} />
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder="Search messages…"
              placeholderTextColor={Palette.inkFaint}
              autoCorrect={false}
              style={[styles.searchInput, webNoOutline]}
            />
            {query ? (
              <Pressable onPress={() => setQuery('')} hitSlop={8}>
                <X size={14} color={Palette.inkFaint} strokeWidth={2} />
              </Pressable>
            ) : null}
          </View>

          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.filterStrip}
            contentContainerStyle={styles.filterStripContent}>
            {FILTERS.map((f) => {
              const sel = filter === f.key;
              const showBadge = f.key === 'unread' && unreadCount > 0;
              return (
                <Pressable
                  key={f.key}
                  onPress={() => setFilter(f.key)}
                  style={({ pressed }) => [
                    styles.filterChip,
                    sel && styles.filterChipSel,
                    pressed && { opacity: 0.9 },
                  ]}>
                  <Text style={[styles.filterChipText, sel && styles.filterChipTextSel]}>
                    {f.label}
                  </Text>
                  {showBadge ? (
                    <View style={[styles.filterBadge, sel && styles.filterBadgeSel]}>
                      <Text
                        style={[
                          styles.filterBadgeText,
                          sel && styles.filterBadgeTextSel,
                        ]}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </Text>
                    </View>
                  ) : null}
                </Pressable>
              );
            })}
          </ScrollView>
        </>
      ) : null}

      <ScrollView
        contentContainerStyle={{ paddingBottom: 60 }}
        showsVerticalScrollIndicator={false}
        onScroll={win.onScroll}
        onLayout={win.onLayout}
        scrollEventThrottle={16}>
        {totalCount === 0 ? (
          <View style={styles.empty}>
            <View style={styles.emptyIcon}>
              <BellOff size={28} color={Palette.inkFaint} strokeWidth={1.6} />
            </View>
            <Text style={styles.emptyTitle}>Inbox zero</Text>
            <Text style={styles.emptyBody}>
              Nothing to read right now. Achievements and weather alerts
              will land here as you use the app.
            </Text>
          </View>
        ) : visible.length === 0 ? (
          <View style={styles.empty}>
            <View style={styles.emptyIcon}>
              <SearchIcon size={26} color={Palette.inkFaint} strokeWidth={1.8} />
            </View>
            <Text style={styles.emptyTitle}>No matches</Text>
            <Text style={styles.emptyBody}>
              Try a different filter or clear the search.
            </Text>
          </View>
        ) : (
          grouped.map((g) => (
            <View key={g.key}>
              <Text style={styles.sectionLabel}>{g.label.toUpperCase()}</Text>
              <View style={styles.list}>
                {g.items.map((m) => (
                  // Swipe-right-to-delete on each row. Disabled in
                  // select mode so multi-select gestures don't fight
                  // each other (tap toggles selection there, not row
                  // open). The swipe fires onDeleteOne — same confirm
                  // dialog the row's footer delete action uses, so
                  // a swipe gives the user the same safety net.
                  <SwipeRow
                    key={m.id}
                    onAction={() => onDeleteOne(m)}
                    disabled={selectMode}>
                    <MessageRow
                      message={m}
                      expanded={expanded.has(m.id)}
                      selectMode={selectMode}
                      selected={selected.has(m.id)}
                      onPress={() => onRowPress(m)}
                      onLongPress={() => onRowLongPress(m)}
                      onDelete={() => onDeleteOne(m)}
                      onToggleRead={() => {
                        if (m.read) void markUnread(db, m.id);
                        else void markRead(db, m.id);
                      }}
                      homeNickname={
                        m.propertyId ? homesById.get(m.propertyId) ?? null : null
                      }
                    />
                  </SwipeRow>
                ))}
              </View>
            </View>
          ))
        )}
        {totalCount > 0 && visible.length > 0 ? (
          <InfiniteFooter hasMore={win.hasMore} total={win.total} loadMore={win.loadMore} noun="message" />
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
}

// ── Topbars ────────────────────────────────────────────────────────────

function BrowseTopbar({
  router,
  unreadCount,
  onSelect,
  onMarkAllRead,
  disableBulkActions,
}: {
  router: ReturnType<typeof useRouter>;
  unreadCount: number;
  onSelect: () => void;
  onMarkAllRead: () => void;
  disableBulkActions: boolean;
}) {
  return (
    <View style={styles.topbarInner}>
      <View style={styles.topbarLeft}>
        <Pressable
          onPress={() => goBackOrTo(router, '/(tabs)')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Pressable
          accessibilityLabel="Go to Home"
          onPress={() => goTo(router, '/(tabs)')}
          style={({ pressed }) => [styles.shortcutBtn, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <HomeIcon size={18} color={Palette.forest} strokeWidth={1.9} />
        </Pressable>
        <Pressable
          accessibilityLabel="Go to Schedule"
          onPress={() => goTo(router, '/(tabs)/schedule')}
          style={({ pressed }) => [styles.shortcutBtn, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <Calendar size={18} color={Palette.forest} strokeWidth={1.9} />
        </Pressable>
      </View>
      <Text style={styles.topbarTitle}>Inbox</Text>
      <View style={styles.topbarRight}>
        <Pressable
          accessibilityLabel="Mark all read"
          disabled={disableBulkActions || unreadCount === 0}
          onPress={onMarkAllRead}
          style={({ pressed }) => [
            styles.shortcutBtn,
            (disableBulkActions || unreadCount === 0) && { opacity: 0.35 },
            pressed && { opacity: 0.7 },
          ]}
          hitSlop={12}>
          <CheckCheck size={17} color={Palette.forest} strokeWidth={2} />
        </Pressable>
        <Pressable
          accessibilityLabel="Select messages"
          disabled={disableBulkActions}
          onPress={onSelect}
          style={({ pressed }) => [
            styles.shortcutBtn,
            disableBulkActions && { opacity: 0.35 },
            pressed && { opacity: 0.7 },
          ]}
          hitSlop={12}>
          <CheckSquare size={17} color={Palette.forest} strokeWidth={2} />
        </Pressable>
      </View>
    </View>
  );
}

function BulkTopbar({
  count,
  onCancel,
  onMarkRead,
  onMarkUnread,
  onDelete,
}: {
  count: number;
  onCancel: () => void;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onDelete: () => void;
}) {
  const disabled = count === 0;
  return (
    <View style={styles.topbarInner}>
      <View style={styles.topbarLeft}>
        <Pressable
          onPress={onCancel}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}
          accessibilityLabel="Exit selection">
          <X size={20} color={Palette.ink} strokeWidth={2} />
        </Pressable>
      </View>
      <Text style={styles.topbarTitle}>
        {count} selected
      </Text>
      <View style={styles.topbarRight}>
        <Pressable
          accessibilityLabel="Mark selected as read"
          disabled={disabled}
          onPress={onMarkRead}
          style={({ pressed }) => [
            styles.shortcutBtn,
            disabled && { opacity: 0.35 },
            pressed && { opacity: 0.7 },
          ]}
          hitSlop={10}>
          <Eye size={17} color={Palette.forest} strokeWidth={2} />
        </Pressable>
        <Pressable
          accessibilityLabel="Mark selected as unread"
          disabled={disabled}
          onPress={onMarkUnread}
          style={({ pressed }) => [
            styles.shortcutBtn,
            disabled && { opacity: 0.35 },
            pressed && { opacity: 0.7 },
          ]}
          hitSlop={10}>
          <EyeOff size={17} color={Palette.forest} strokeWidth={2} />
        </Pressable>
        <Pressable
          accessibilityLabel="Delete selected"
          disabled={disabled}
          onPress={onDelete}
          style={({ pressed }) => [
            styles.shortcutBtn,
            styles.shortcutBtnDanger,
            disabled && { opacity: 0.35 },
            pressed && { opacity: 0.7 },
          ]}
          hitSlop={10}>
          <Trash2 size={16} color={Palette.cream} strokeWidth={2} />
        </Pressable>
      </View>
    </View>
  );
}

// ── Row ────────────────────────────────────────────────────────────────

const TONE_META: Record<
  MessageTone,
  { Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>; tint: string; chipBg: string }
> = {
  info: { Icon: Sparkles, tint: Palette.forest, chipBg: '#EAF0EA' },
  achievement: { Icon: Sparkles, tint: Palette.honeyDeep, chipBg: '#F3DDAE' },
  warning: { Icon: TriangleAlert, tint: Palette.clay, chipBg: 'rgba(176,95,60,0.15)' },
  success: { Icon: Check, tint: Palette.moss, chipBg: '#DCE7E1' },
};

function MessageRow({
  message,
  expanded,
  selectMode,
  selected,
  onPress,
  onLongPress,
  onDelete,
  onToggleRead,
  homeNickname,
}: {
  message: AppMessage;
  expanded: boolean;
  selectMode: boolean;
  selected: boolean;
  onPress: () => void;
  onLongPress: () => void;
  onDelete: () => void;
  onToggleRead: () => void;
  /** Set when the message is tagged with property_id AND the user has
   *  multiple homes. Renders as a forest-tinted chip next to the
   *  timestamp so the user can see at a glance which home the message
   *  refers to without having to switch and check. */
  homeNickname: string | null;
}) {
  const meta = TONE_META[message.tone];
  const unread = !message.read;
  return (
    <Pressable
      onPress={onPress}
      onLongPress={onLongPress}
      delayLongPress={350}
      style={({ pressed }) => [
        styles.row,
        unread && styles.rowUnread,
        selected && styles.rowSelected,
        pressed && { opacity: 0.92 },
      ]}>
      {/* Unread dot — sits on the left edge so a glance down the list
          reads as "what's new" at a glance. Hidden when read or selecting. */}
      {!selectMode && unread ? <View style={styles.unreadDot} /> : null}
      {selectMode ? (
        <View style={[styles.checkbox, selected && styles.checkboxOn]}>
          {selected ? (
            <CheckSquare size={14} color={Palette.cream} strokeWidth={2.4} />
          ) : (
            <Square size={14} color={Palette.forest} strokeWidth={2} />
          )}
        </View>
      ) : null}
      <View style={[styles.rowIcon, { backgroundColor: meta.chipBg }]}>
        <meta.Icon size={15} color={meta.tint} strokeWidth={2} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={[styles.rowTitle, unread && styles.rowTitleUnread]}
          numberOfLines={expanded ? undefined : 2}>
          {message.title}
        </Text>
        {message.body ? (
          <Text style={styles.rowBody} numberOfLines={expanded ? undefined : 1}>
            {message.body}
          </Text>
        ) : null}
        <View style={styles.rowFoot}>
          <Text style={styles.rowMeta}>{relativeTime(message.ts)}</Text>
          {homeNickname ? (
            <View style={styles.homeChip}>
              <Text style={styles.homeChipText} numberOfLines={1}>
                {homeNickname}
              </Text>
            </View>
          ) : null}
          {expanded && !selectMode ? (
            <View style={styles.rowFootActions}>
              <Pressable
                onPress={onToggleRead}
                hitSlop={6}
                style={({ pressed }) => [styles.footAction, pressed && { opacity: 0.7 }]}
                accessibilityLabel={unread ? 'Mark read' : 'Mark unread'}>
                {unread ? (
                  <Eye size={13} color={Palette.forest} strokeWidth={1.9} />
                ) : (
                  <EyeOff size={13} color={Palette.forest} strokeWidth={1.9} />
                )}
                <Text style={styles.footActionText}>
                  {unread ? 'Mark read' : 'Mark unread'}
                </Text>
              </Pressable>
              <Pressable
                onPress={onDelete}
                hitSlop={6}
                style={({ pressed }) => [
                  styles.footAction,
                  styles.footActionDanger,
                  pressed && { opacity: 0.7 },
                ]}
                accessibilityLabel="Delete message">
                <Trash2 size={13} color={Palette.clay} strokeWidth={1.9} />
                <Text style={[styles.footActionText, { color: Palette.clay }]}>
                  Delete
                </Text>
              </Pressable>
            </View>
          ) : null}
        </View>
      </View>
    </Pressable>
  );
}

function relativeTime(t: number): string {
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ── Styles ─────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
    paddingBottom: Spacing.two,
  },
  topbarInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  topbarLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  topbarRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  shortcutBtn: {
    width: 32, height: 32, borderRadius: 16, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  shortcutBtnDanger: {
    backgroundColor: Palette.clay,
    borderColor: Palette.clay,
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },

  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    marginHorizontal: Spacing.four, marginTop: Spacing.one,
    backgroundColor: Palette.card,
    borderWidth: 1.5, borderColor: Palette.line,
    borderRadius: 12,
    paddingHorizontal: 12, paddingVertical: 10,
  },
  searchInput: {
    flex: 1,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
    padding: 0,
  },

  filterStrip: {
    marginTop: 10,
    height: 44,
    flexGrow: 0,
    flexShrink: 0,
  },
  filterStripContent: {
    paddingHorizontal: Spacing.four,
    alignItems: 'center',
    gap: 6,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 32,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  filterChipSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  filterChipText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink },
  filterChipTextSel: { color: Palette.cream },
  filterBadge: {
    minWidth: 18,
    height: 18,
    paddingHorizontal: 5,
    borderRadius: 9,
    backgroundColor: Palette.clay,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterBadgeSel: {
    backgroundColor: Palette.honey,
  },
  filterBadgeText: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.cream,
    lineHeight: 13,
  },
  filterBadgeTextSel: {
    color: '#3A2A0C',
  },

  sectionLabel: {
    marginTop: Spacing.four,
    marginHorizontal: 22,
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.inkFaint,
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  list: {
    marginHorizontal: Spacing.four,
    gap: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 11,
    padding: 12,
    paddingLeft: 14,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  // Unread state — subtle background lift + the accent dot on the left
  // edge does the heavy lifting. Don't change the title color, just the
  // weight.
  rowUnread: {
    backgroundColor: '#FCF5E2',
    borderColor: '#E7CC91',
  },
  rowSelected: {
    borderColor: Palette.forest,
    borderWidth: 2,
    backgroundColor: '#EEF3EE',
  },
  unreadDot: {
    position: 'absolute',
    left: 6,
    top: 16,
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: Palette.clay,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: Palette.line,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  checkboxOn: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  rowIcon: {
    width: 30,
    height: 30,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowTitle: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13.5,
    color: Palette.ink,
  },
  rowTitleUnread: {
    fontFamily: Fonts.sansBold,
  },
  rowBody: {
    marginTop: 3,
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    color: Palette.inkSoft,
    lineHeight: 18,
  },
  rowFoot: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 6,
    gap: 8,
    flexWrap: 'wrap',
  },
  rowMeta: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.inkFaint,
    letterSpacing: 0.4,
  },
  // Multi-home "from Lake House" chip — small forest pill so the user
  // can scan the inbox and see which property each message refers to
  // without having to switch homes and check.
  homeChip: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 99,
    backgroundColor: '#EAF0EA',
    borderWidth: 1,
    borderColor: 'rgba(45,78,54,0.25)',
    maxWidth: 140,
  },
  homeChipText: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.forest,
    letterSpacing: 0.3,
  },
  rowFootActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  footAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: '#EAF0EA',
  },
  footActionDanger: {
    backgroundColor: 'rgba(176,95,60,0.13)',
  },
  footActionText: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.forest,
    letterSpacing: 0.2,
  },

  empty: {
    marginTop: Spacing.six,
    marginHorizontal: Spacing.four,
    padding: 24,
    borderRadius: Radius.xl,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    alignItems: 'center',
    gap: 10,
  },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 17,
    color: Palette.ink,
    marginTop: 4,
  },
  emptyBody: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    textAlign: 'center',
    lineHeight: 19,
  },
});
